import { BookOpen, Loader2, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { cn } from '../lib/cn';
import { useSkills } from '../lib/skills';

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (skillName: string) => void;
};

/**
 * Small overlay listing every Claude skill available to this
 * profile (`docx`, `pptx`, `canvas-design`, …). Click a row to
 * nudge the agent via a prefix like "Use the docx skill to …".
 */
export function SkillsDrawer({ open, onClose, onPick }: Props) {
  const { data: skills, isLoading } = useSkills();
  const wrapRef = useRef<HTMLDivElement>(null);
  useFocusTrap(wrapRef, open);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="mc-fade-enter fixed inset-0 z-40 flex items-end justify-center bg-background/60 backdrop-blur-sm md:items-start md:p-6">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Закрыть"
        onClick={onClose}
      />
      <div
        ref={wrapRef}
        // biome-ignore lint/a11y/useSemanticElements: custom overlay drawer
        role="dialog"
        aria-modal="true"
        aria-labelledby="skills-drawer-title"
        className="mc-sheet-enter relative z-10 mc-safe-bottom max-h-[85vh] w-full overflow-hidden rounded-t-2xl border border-border bg-surface shadow-lg md:mt-16 md:max-w-lg md:rounded-2xl [@media(orientation:landscape)]:max-h-[95vh]"
      >
        <header className="flex items-center gap-2 border-b border-border px-4 py-3">
          <BookOpen size={14} className="text-muted-foreground" />
          <span id="skills-drawer-title" className="text-[14px] font-medium">
            Навыки
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-subtle"
            title="Закрыть"
            aria-label="Закрыть навыки"
          >
            <X size={14} />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto p-1.5">
          {isLoading && (
            <div className="flex items-center gap-2 px-3 py-4 text-[13px] text-muted-foreground">
              <Loader2 size={13} className="animate-spin" />
              Загрузка навыков…
            </div>
          )}
          {skills?.length === 0 && !isLoading && (
            <div className="px-3 py-4 text-[13px] text-muted-foreground">
              В этом профиле нет установленных навыков.
            </div>
          )}
          {skills?.map((s) => (
            <button
              key={s.dir}
              type="button"
              onClick={() => onPick(s.name)}
              className={cn(
                'block w-full rounded-lg px-3 py-2.5 text-left transition hover:bg-subtle',
              )}
            >
              <div className="font-mono text-[13px] font-medium">{s.name}</div>
              {s.description && (
                <div className="mt-0.5 line-clamp-2 text-[12px] text-muted-foreground">
                  {s.description}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
